<template>
  <div
    ref="mapRef"
    class="map"
  >
    <SightsMapCardForPopup
      v-if="card && isViewPopup"
      :key="`map-card-${card.id}`"
      class="card map-popup"
      max-width="327px"
      :sight="card"
    />
    <yandex-map
      v-if="isVisible"
      v-model="map"
      class="yandex-map"
      cursor-grab
      :settings="{
        location: {
          ...LOCATION,
          duration: 1000,
        },
        showScaleInCopyrights: true,
        behaviors: enabledBehaviors,
      }"
      :width="width"
      :height="height"
    >
      <yandex-map-controls :settings="{ position: 'right' }">
        <yandex-map-zoom-control />
        <yandex-map-geolocation-control />
      </yandex-map-controls>
      <YandexMapDefaultFeaturesLayer />
      <YandexMapDefaultSchemeLayer :settings="{ theme: 'light', visible: false, source: 'scheme' }" />
      <YandexMapLayer :settings="{ zIndex: 1, source: 'scheme', type: 'ground' }" />
      <YandexMapLayer :settings="{ zIndex: 1, source: 'scheme', type: 'labels' }" />
      <YandexMapLayer :settings="{ zIndex: 1, source: 'scheme', type: 'buildings' }" />
      <YandexMapListener :settings="{ onUpdate: update }" />

      <yandex-map-clusterer
        zoom-on-cluster-click
        :cluster-marker-props="{ position: 'default' }"
        :grid-size="128"
      >
        <yandex-map-marker
          v-for="(marker, index) in mappableSights"
          :key="`${marker.id} - ${marker.isHovered}`"
          position="top-center left-center"
          class="yandex-map-marker"
          :data-index="index"
          :settings="{
            coordinates: [marker.longitude, marker.latitude],
            zIndex: marker.isHovered ? 2 : 0,
            hideOutsideViewport: true,
            properties: {
              isHovered: marker.isHovered,
            },
          }"
          @click="[useMarker(marker), enterToMarker($event, marker)]"
        >
          <div class="map-img">
            <Marker
              :marker="marker"
              :only-marker="onlyMarker"
            />
          </div>
        </yandex-map-marker>
        <template #cluster="{ length, clusterer }">
          <div
            :class="[
              'cluster',
              'fade-in',
              {
                'cluster-hover': checkClusterHovered(clusterer.features),
              },
            ]"
          >
            {{ length }}
          </div>
        </template>
      </yandex-map-clusterer>
    </yandex-map>
  </div>
</template>
<script setup lang="ts">
  import { computed, shallowRef, ref, onMounted, toRaw, watch, useTemplateRef, nextTick } from 'vue';
  import {
    YandexMap,
    YandexMapDefaultSchemeLayer,
    initYmaps,
    YandexMapMarker,
    YandexMapDefaultFeaturesLayer,
    YandexMapListener,
    YandexMapLayer,
    YandexMapClusterer,
    YandexMapControls,
    YandexMapZoomControl,
    YandexMapGeolocationControl,
    getBoundsFromCoords,
  } from 'vue-yandex-maps';

  import { trackEvent } from '@/services/analytics';
  import { type CityTagsAdvanced, type ModalProps } from '@/shared/models/types';
  import { useSeoStore } from '@/shared/store/seo';
  import SightsMapCardForPopup from '@/shared/ui/SightsMapCardForPopup.vue';
  import Marker from '@/shared/ui/SightsMapMarker.vue';
  import { useScreenSize } from '@/shared/utils/breakpoints';
  import debounce from '@/shared/utils/debounce';

  import type { LngLat, MapEventUpdateHandler, ReadonlyLngLat, YMap, YMapLocationRequest } from '@yandex/ymaps3-types';
  import type { Feature } from '@yandex/ymaps3-types/packages/clusterer';

  const props = withDefaults(defineProps<ModalProps>(), {
    width: '100%',
    height: '100%',
    sights: () => [],
    enabledBehaviors: () => ['drag', 'pinchZoom', 'dblClick'],
    onlyMarker: false,
  });

  const map = shallowRef<null | YMap>(null);
  const zoomStart = ref(0);
  const activeZoom = ref(0);
  const emit = defineEmits(['isViewSights', 'mapIsLoading', 'update', 'clickOnMarker']);
  const bounds = ref();
  const mapReference = useTemplateRef('mapRef');
  const card = ref<CityTagsAdvanced | null>(null);
  const isViewPopup = ref(false);
  const blurMarkerTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  const lastHoveredMarker = ref();
  const isVisible = ref(false);

  const popupTopPosition = ref('0');
  const popupLeftPosition = ref('0');
  const seoStore = useSeoStore();
  const coords = ref<LngLat[]>([]);
  const center = ref<LngLat>([0, 0]);

  type MappableSight = CityTagsAdvanced & Required<Pick<CityTagsAdvanced, 'latitude' | 'longitude'>>;

  const isMappableSight = (sight: CityTagsAdvanced): sight is MappableSight =>
    typeof sight.longitude === 'number' && typeof sight.latitude === 'number';

  const mappableSights = computed(() => props.sights.filter(isMappableSight));

  const addListener = () => {
    mapReference.value?.addEventListener('mousemove', (event) => {
      const markerElement = event.target instanceof Element ? event.target.closest('.yandex-map-marker') : null;
      const currentMarker = props.sights[lastHoveredMarker.value];

      if (markerElement instanceof HTMLElement) {
        if (currentMarker?.isHovered) {
          return;
        }

        hover(markerElement.dataset.index ?? null, true);
      } else {
        if (!currentMarker?.isHovered) {
          return;
        }

        hover(null, false);
      }
    });

    mapReference.value?.addEventListener('click', (event) => {
      const markerElement = event.target instanceof Element ? event.target.closest('.yandex-map-marker') : null;

      if (!markerElement) {
        closePopup();
      }
    });
  };

  const LOCATION = ref<YMapLocationRequest>({
    center: [37.617698, 55.755864],
    zoom: 15,
  });

  onMounted(async () => {
    await nextTick();
    checkCoords();

    bounds.value = getBoundsFromCoords(coords.value.length === 1 ? [...coords.value, ...coords.value] : coords.value);
    LOCATION.value = { bounds: bounds.value, duration: LOCATION.value.duration, easing: LOCATION.value.easing };

    await initYmaps();

    setTimeout(() => {
      if (map.value) {
        zoomStart.value = map.value.zoom;
        center.value = map.value.center as LngLat;
      }
      useZoom();
    }, 0);

    void trackEvent('sights:map.view', toRaw(seoStore.geo));
    addListener();
    isVisible.value = true;
  });

  const checkCoords = () => {
    coords.value = mappableSights.value.reduce<LngLat[]>((accumulator, currentValue) => {
      const { longitude, latitude } = currentValue;
      const coordinates = [longitude, latitude] as LngLat;
      accumulator.push(coordinates);

      return accumulator;
    }, []);
  };

  watch(
    map,
    (val) => {
      if (val) {
        activeZoom.value = val.zoom;
        emit('mapIsLoading');
      }
    },
    {
      deep: true,
      immediate: true,
    },
  );

  // Отдалить на максимум
  const useZoom = () => {
    if (props.strictZoom) {
      LOCATION.value = { zoom: props.strictZoom, center: center.value };

      return;
    }

    const min = zoomStart.value - 1.1;
    const max = zoomStart.value - 1;

    LOCATION.value = {
      zoom: Math.random() * (min - max) + min,
      center: center.value,
    };
  };

  defineExpose({ useZoom });

  type UpdateObject = Parameters<MapEventUpdateHandler>[0];

  const checkMarker = (mapData: UpdateObject) => {
    const locationBounds = mapData.location.bounds as Readonly<(typeof mapData)['location']['bounds']>;

    mappableSights.value.forEach((marker) => {
      const coordinates = [marker.longitude, marker.latitude] as ReadonlyLngLat;

      marker.isView =
        locationBounds[0][0] < coordinates[0] &&
        coordinates[0] < locationBounds[1][0] &&
        locationBounds[0][1] > coordinates[1] &&
        coordinates[1] > locationBounds[1][1];
    });

    emit('isViewSights', props.sights);
  };

  const update = (mapData: UpdateObject) => {
    debounce(checkMarker, mapData);
    activeZoom.value = mapData.location.zoom;
    emit('update');
  };

  const { isTabletOnly, isMobile } = useScreenSize();

  const isMobileOrTablet = computed(() => {
    return isTabletOnly.value || isMobile.value;
  });

  const hover = (index: string | null, value: boolean) => {
    if (isMobileOrTablet.value) {
      return;
    }

    if (index) {
      lastHoveredMarker.value = index;
    }

    const sight = props.sights[lastHoveredMarker.value];

    if (sight) {
      sight.isHovered = value;
    }
  };

  const checkClusterHovered = (value: Feature[]) => {
    return value.find((el) => el.properties?.isHovered);
  };

  const enterToMarker = (event: Event, marker: CityTagsAdvanced) => {
    if (props.hidePopup) {
      return;
    }

    clearTimeout(blurMarkerTimer);

    card.value = marker;

    if (!mapReference.value) {
      return;
    }

    const mapPosition = mapReference.value.getBoundingClientRect();
    const marketPosition = (event.target as HTMLDivElement).getBoundingClientRect();
    const top = Math.floor(marketPosition.top - mapPosition.top);
    const left = Math.floor(marketPosition.left - mapPosition.left);
    const right = Math.ceil(mapPosition.right - marketPosition.right);
    const bottom = Math.ceil(mapPosition.bottom - marketPosition.bottom);

    const cardWidth = 327;
    const cardHeight = 160;
    const markerSize = 40;

    const setPosition = (topPos: number, leftPos: number) => {
      popupTopPosition.value = `${String(topPos)}px`;
      popupLeftPosition.value = `${String(leftPos)}px`;
    };

    if (top - cardHeight > 0 && left - cardWidth / 2 > 0 && right - cardWidth / 2 > 0) {
      setPosition(top - cardHeight, left - cardWidth / 2);
    } else if (bottom - cardHeight > 0 && left - cardWidth / 2 > 0 && right - cardWidth / 2 > 0) {
      setPosition(top + 28, left - cardWidth / 2);
    } else if (left - cardWidth > 0 && bottom - cardHeight / 2 > 0 && top - cardHeight / 2 > 0) {
      setPosition(top - (cardHeight - markerSize) / 2, left - cardWidth - 28);
    } else if (right - cardWidth > 0 && bottom - cardHeight / 2 > 0 && top - cardHeight / 2 > 0) {
      setPosition(top - (cardHeight - markerSize) / 2, left + 28);
    } else if (top < cardHeight / 2 && left < cardWidth / 2) {
      setPosition(top + 16, left + 16);
    } else if (bottom < cardHeight / 2 && left < cardWidth / 2) {
      setPosition(top - cardHeight + markerSize / 2, left + markerSize / 2);
    } else if (top < cardHeight / 2 && right < cardWidth / 2) {
      setPosition(top + 28, left - cardWidth);
    } else if (bottom < cardHeight / 2 && right < cardWidth / 2) {
      setPosition(top - cardHeight, left - cardWidth);
    }

    isViewPopup.value = true;
  };

  const useMarker = (marker: CityTagsAdvanced) => {
    emit('clickOnMarker', marker);
    void trackEvent('sights:map.click', {
      ...seoStore.geo,
      tag_id: marker.id ?? 0,
      tag_name: marker.name ?? '',
    });
  };

  const closePopup = () => {
    isViewPopup.value = false;
  };
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as legacy-typography;
  @use '@experience/shared/dist/assets/adaptivity' as legacy-adaptivity;

  .map-img {
    width: 40px;
    height: 40px;
  }

  .cluster {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    color: var(--alias-color-text-inverse);
    text-align: center;
    cursor: pointer;
    background: var(--alias-color-background-inverse);
    border: 1px rgb(255 255 255) solid;
    border-radius: 100%;

    &:hover {
      background: var(--alias-color-icon-accent-magenta);

      @include legacy-adaptivity.mobile {
        background: var(--alias-color-background-inverse);
      }
    }

    @include legacy-typography.base-200-100-medium;
  }

  .cluster-hover {
    background: var(--alias-color-icon-accent-magenta);
  }

  .yandex-map {
    background-color: var(--alias-color-skeleton-default);
  }

  .map {
    width: 100%;
  }

  .map-popup {
    position: absolute;
    top: v-bind('popupTopPosition');
    left: v-bind('popupLeftPosition');
    z-index: 9;
    padding: 8px;
  }
</style>
